import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { effectivePrice, type DeliveryAddress, type Product, type ProductQuery } from "@neus/shared";
import { useInventory } from "../store/inventory";
import { api, ApiError } from "./client";
import { features } from "../features";

export const productsKey = (query: ProductQuery) => ["products", query] as const;
export const FACETS_KEY = ["facets"] as const;

/** The list a `products` section shows. */
export const popularProductsQuery = ({ count, department }: { count: number; department?: string }): ProductQuery => ({
  sort: "popular",
  pageSize: count,
  inStock: "true",
  department,
});

export function useProducts(query: ProductQuery) {
  return useQuery({
    queryKey: productsKey(query),
    queryFn: () => api.products(query),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(sku: string | undefined) {
  return useQuery({
    queryKey: ["product", sku],
    queryFn: () => api.product(sku!),
    enabled: Boolean(sku),
  });
}

/** `value`, updated only after it has stopped changing for `ms`. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Type-ahead suggestions for the search box (debounced; nothing under 2 characters). The data
 * carries the term it's for, because a previous term's results stay in place while the next loads.
 */
export function useSearchSuggestions(q: string, limit = 8) {
  const term = useDebouncedValue(q.trim(), 150);
  return useQuery({
    queryKey: ["suggest", term, limit],
    queryFn: async () => ({ term, items: await api.suggest(term, limit) }),
    enabled: term.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * What the signed-in shopper pays for a product: their contractor price when staff set one (with
 * the usual price for the strikethrough), else the price. The server charges the same.
 */
export function useEffectivePrice(product: { sku: string; price: number } | undefined) {
  const { data: me } = useMe();
  return product ? effectivePrice(product, me) : undefined;
}

/** The signed-in user (null when signed out). */
export function useMe() {
  // With accounts off, nobody can sign in: always signed out, without asking the API.
  return useQuery({
    queryKey: ["me"],
    queryFn: features.accounts ? api.me : async () => null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Sign out here (or on every device), then end the Entra session too. Throws if it failed. */
export function useSignOut() {
  const qc = useQueryClient();
  return async (everywhere = false) => {
    const { signOutUrl } = await api.logout(everywhere);
    qc.setQueryData(["me"], null);
    // The next sign-in asks again; Entra brings the browser back to the home page.
    window.location.assign(signOutUrl);
  };
}

/** Retries server and network failures, but not answers like 401 or 404 that won't change. */
const retryUnlessClientError = (failures: number, err: unknown) =>
  failures < 3 && !(err instanceof ApiError && err.status >= 400 && err.status < 500);

/** The signed-in shopper's store orders. */
/** Which version the API reports, for the footer. It can't change while it's running, so it's asked once. */
export function useSiteVersion() {
  return useQuery({ queryKey: ["site-version"], queryFn: api.version, retry: false, staleTime: Infinity });
}

/** Staff only: the pricing for all contractors (null when there's none). */
export function useAllContractorPricing() {
  return useQuery({
    queryKey: ["admin-all-pricing"],
    queryFn: api.allContractorPricing,
    retry: false,
    enabled: features.allContractorPricing,
  });
}

export function useMyOrders() {
  return useQuery({ queryKey: ["my-orders"], queryFn: api.myOrders, retry: retryUnlessClientError });
}

/**
 * Server-priced quote for an order being built (debounced; keeps the last quote while re-pricing).
 * `current` is false until the quote matches the lines as they are now.
 */
export function useContractorQuote(lines: { sku: string; qty: number }[], deliveryAddress?: DeliveryAddress) {
  const latest = JSON.stringify({ lines, deliveryAddress });
  const key = useDebouncedValue(latest, 300);
  const body = JSON.parse(key) as { lines: { sku: string; qty: number }[]; deliveryAddress?: DeliveryAddress };
  const query = useQuery({
    queryKey: ["contractor-quote", key],
    queryFn: () => api.contractorQuote(body.lines, body.deliveryAddress),
    // The debounced body, not the live lines: right after the first product is added it's still empty.
    enabled: body.lines.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
  return { query, current: key === latest && !query.isPlaceholderData && query.isSuccess };
}

export function useContractorOrders() {
  return useQuery({ queryKey: ["contractor-orders"], queryFn: api.contractorOrders, retry: retryUnlessClientError });
}

export function useContractorOrder(id: string, sessionId?: string) {
  return useQuery({ queryKey: ["contractor-order", id, sessionId], queryFn: () => api.contractorOrder(id, sessionId) });
}

export function useFacets() {
  return useQuery({ queryKey: FACETS_KEY, queryFn: api.facets, staleTime: 5 * 60_000 });
}

const INVENTORY_POLL_MS = 60_000;

/**
 * Keeps on-screen stock fresh without refetching product lists: polls the
 * server for inventory changes since the last version we saw. When the server
 * has done a full catalog refresh (reset), product queries are refetched.
 */
export function useInventorySync() {
  const qc = useQueryClient();
  const version = useInventory((s) => s.version);
  const apply = useInventory((s) => s.apply);
  const reset = useInventory((s) => s.reset);

  const { data } = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api.inventory(useInventory.getState().version ?? 0),
    refetchInterval: INVENTORY_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (!data) return;
    if (data.reset) {
      if (version !== null) {
        void qc.invalidateQueries({ queryKey: ["products"] });
        void qc.invalidateQueries({ queryKey: ["product"] });
      }
      reset(data.version);
    } else if (data.version !== version) {
      apply(data.version, data.changes);
    }
  }, [data, version, apply, reset, qc]);
}

/** Current stock for a product, taking live inventory updates into account. */
export function useStock(product: Pick<Product, "sku" | "inventory"> | undefined): number {
  const live = useInventory((s) => (product ? s.overlay[product.sku] : undefined));
  return live ?? product?.inventory ?? 0;
}
